package sarka.design.skoria.friends

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import sarka.design.skoria.network.TokenStore
import sarka.design.skoria.network.createPlatformHttpClient
import sarka.design.skoria.network.defaultApiUrl

/**
 * HTTP client for the friend system.
 *
 * Follows the same shape as [sarka.design.skoria.messaging.MessageApi]: every
 * call is authenticated with the bearer token and wrapped so failures come back
 * as a [Result] rather than a thrown exception.
 */
class FriendApi(
    private val baseUrl: String = defaultApiUrl(),
    private val httpClient: HttpClient = createPlatformHttpClient(),
) {

    suspend fun overview(): Result<FriendsOverview> = call {
        httpClient.get("$baseUrl/friends") { auth() }.body()
    }

    /**
     * Sends a friend request. Distinguishes the outcomes the UI needs to react
     * to differently: a plain send, accepting a request the other side already
     * had, and the various rejections.
     */
    suspend fun sendRequest(username: String): Result<Unit> = call {
        val response = httpClient.post("$baseUrl/friends/request") {
            auth()
            contentType(ContentType.Application.Json)
            setBody(FriendActionRequest(username))
        }

        when (response.status) {
            HttpStatusCode.OK -> Unit
            HttpStatusCode.NotFound -> throw FriendError.UserNotFound(username)
            HttpStatusCode.Conflict -> throw FriendError.Conflict(response.body<Map<String, String>>()["message"] ?: "Can't send that request")
            HttpStatusCode.TooManyRequests -> throw FriendError.RateLimited
            else -> throw FriendError.Server(response.status.value)
        }
    }

    suspend fun accept(username: String): Result<Unit> = post("/friends/accept", username)
    suspend fun decline(username: String): Result<Unit> = post("/friends/decline", username)
    suspend fun cancel(username: String): Result<Unit> = post("/friends/cancel", username)
    suspend fun unfriend(username: String): Result<Unit> = post("/friends/unfriend", username)
    suspend fun block(username: String): Result<Unit> = post("/friends/block", username)
    suspend fun unblock(username: String): Result<Unit> = post("/friends/unblock", username)

    suspend fun blocked(): Result<List<String>> = call {
        httpClient.get("$baseUrl/friends/blocked") { auth() }
            .body<Map<String, List<String>>>()["blocked"] ?: emptyList()
    }

    private suspend fun post(path: String, username: String): Result<Unit> = call {
        val response = httpClient.post("$baseUrl$path") {
            auth()
            contentType(ContentType.Application.Json)
            setBody(FriendActionRequest(username))
        }
        if (!response.status.isSuccess()) {
            when (response.status) {
                HttpStatusCode.NotFound -> throw FriendError.UserNotFound(username)
                HttpStatusCode.TooManyRequests -> throw FriendError.RateLimited
                else -> throw FriendError.Server(response.status.value)
            }
        }
    }

    private fun HttpRequestBuilder.auth() {
        TokenStore.token.value?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }

    private inline fun <T> call(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: Throwable) {
        Result.failure(e.asFriendError())
    }
}

/** Failures worth distinguishing in the UI. */
sealed class FriendError(message: String) : Exception(message) {
    class UserNotFound(val username: String) : FriendError("No user called $username")
    /** A rejection the server explained: already friends, cooldown, too many pending. */
    class Conflict(message: String) : FriendError(message)
    object RateLimited : FriendError("You're doing that too quickly. Please slow down.")
    object NotAuthenticated : FriendError("Session expired. Please log in again.")
    object Offline : FriendError("Could not reach the server")
    class Server(val status: Int) : FriendError("Server error ($status)")
}

private fun Throwable.asFriendError(): Throwable = when {
    this is FriendError -> this
    message?.contains("401") == true -> FriendError.NotAuthenticated
    message?.contains("fetch", ignoreCase = true) == true -> FriendError.Offline
    message?.contains("Connection refused", ignoreCase = true) == true -> FriendError.Offline
    else -> this
}
